import api from '@/lib/api';
import {
  MercadoPagoFeeInfo,
  MercadoPagoCredentials,
} from '@/types/mercadopago.model';
import { userService } from '../user/userApi';
import { toast } from '@/components/ui/use-toast';
import { handleGlobalError } from '@/utils/error-handler';

const CODE_SPLIT = '_verC_' as const;

function base64URLEncode(buffer: ArrayBuffer): string {
  return btoa(
    String.fromCharCode.apply(
      null,
      new Uint8Array(buffer) as unknown as number[],
    ),
  )
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array.buffer);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(digest);
}

export const mercadoPagoService = {
  getCredentials: async (): Promise<MercadoPagoCredentials> => {
    try {
      const response = await api.get<MercadoPagoCredentials>(
        '/mercadopago/credentials',
      );

      const { appId, redirectUri } = response.data;

      // Validate that required values are present
      if (!appId || !redirectUri) {
        const missingFields = [];
        if (!appId) missingFields.push('appId');
        if (!redirectUri) missingFields.push('redirectUri');

        toast({
          variant: 'destructive',
          title: 'Error de configuración',
          description: `Faltan las siguientes credenciales de MercadoPago: ${missingFields.join(', ')}. Por favor, contacta al administrador.`,
        });

        throw new Error(
          `Missing MercadoPago credentials: ${missingFields.join(', ')}`,
        );
      }

      return response.data;
    } catch (error) {
      // If it's not a validation error, use the global handler with custom message
      if (
        !(error instanceof Error) ||
        !error.message?.includes('Missing MercadoPago credentials')
      ) {
        handleGlobalError(
          error,
          'Error de conexión',
          'No se pudieron obtener las credenciales de MercadoPago. Por favor, intenta nuevamente.',
        );
      }

      throw error;
    }
  },

  generateAuthUrl: async () => {
    const credentials = await mercadoPagoService.getCredentials();
    const { appId: APP_ID, redirectUri: REDIRECT_URI } = credentials;

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = `${codeChallenge}${CODE_SPLIT}${codeVerifier}`;
    const authUrl = `https://auth.mercadopago.com.ar/authorization?client_id=${APP_ID}&response_type=code&platform_id=mp&code_challenge_method=S256&code_challenge=${codeChallenge}&redirect_uri=${REDIRECT_URI}&state=${state}`;
    return { authUrl, state };
  },

  handleAuthorization: async (code: string, state: string) => {
    const [codeChallenge, codeVerifier] = state.split(CODE_SPLIT);
    try {
      const response = await api.post('/mercadopago/auth', null, {
        params: {
          code,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge,
        },
      });
      return response;
    } catch (error) {
      console.error('Error al obtener el token de acceso:', error);

      throw error;
    }
  },

  /**
   * Checks if the user has MercadoPago credentials configured
   */
  hasMercadoPagoCredentials: async (): Promise<boolean> => {
    try {
      const user = await userService.getCurrentUser();
      return user.hasMercadoPagoCredentials;
    } catch (error) {
      console.error('Error checking MercadoPago credentials:', error);
      return false;
    }
  },

  getFeeInfo: async (): Promise<MercadoPagoFeeInfo> => {
    try {
      const response = await api.get<MercadoPagoFeeInfo>('/mercadopago/fee');
      return response.data;
    } catch (error) {
      console.error('Error fetching MercadoPago fee information:', error);

      throw error;
    }
  },
};
